import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  Container,
  Paper,
  useTheme,
  Divider,
  CircularProgress,
  lighten,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import Styles from '../../../theme/commonCss/Styles';
import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';
import ajvKeywords from 'ajv-keywords';
import {
  checklistTagsSchema,
  activitiesListSchema,
  checklistNameSchema,
} from './form';
import get from 'lodash/get';
import FormModal from '../../../common/formModal';
import { palette } from '../../../theme/palette';
import { ReactComponent as PlusCircleIcon } from '../../../assets/icons/main/plus-circle-icon.svg';
import { ReactComponent as SaveIcon } from '../../../assets/icons/main/save-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/main/create-icon.svg';
import Dnd from './Dnd';
import TagsTable from './tagsTable';
import FileExport from '../../../common/FileExport';
import ExcelImport from './ExcelImport';
import { maintenanceManagementActions } from '../../../store/reducers/maintenance-management';
import PopUp from '../../../components/Popup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { pageActions } from '../../../store/reducers/page-actions';
import lightOrdarkColor from '@check-light-or-dark/color';
import { ReactComponent as VrLine } from '../../../assets/icons/main/verticalLine.svg';
import { ReactComponent as SuccessIcon } from '../../../assets/icons/main/successIcon.svg';

interface ActivitiesListTypes {
  id: number;
  activity_name: string;
  pre_requisites: string;
  description: string;
  documents: [] | { name: string; file: string }[];
}

const RequiredFieldStar = () => {
  return <Typography component='span' sx={{ fontWeight: 400, fontSize: 16, color: 'red' }}>*</Typography>;
}

const AddEditActivity = ({
  activityDataLoading,
  activityData,
  addActivityLoading,
  addActivitySuccess,
  updateActivityLoading,
  updateActivitySuccess,
  viewFromModal,
  activityIdFromModal,
}: any) => {
  const dispatch = useDispatch();
  const ajv = new Ajv({ allErrors: true });
  AjvErrors(ajv);
  ajvKeywords(ajv, 'uniqueItemProperties');
  const [ajvErrors, setAjvErrors] = useState<any>({});
  const navigate = useNavigate();
  const themeData: any = useTheme();
  const [checklistName, setChecklistName] = useState('');
  const [saveAs, setSaveAs] = useState({ open: false, checklistName: '' });
  const [activitiesList, setActivitiesList] = useState<ActivitiesListTypes[]>([]);
  const [tags, setTags] = useState<{ id: number; key: string; value: string }[]>([{ id: 1, key: '', value: '' }]);
  const [excelImportStatus, setExcelImportStatus] = useState({
    open: false,
    message: '',
  });
  const [popupStatus, setPopupStatus] = useState({ open: false, message: '' });
  const location = useLocation();
  const params = useParams();
  const checkActivityId = activityIdFromModal || get(params, 'activityId', '');
  const readOnly = viewFromModal || location.pathname.includes('view');
  const isEdit = location.pathname.includes('edit');
  const theme :any = useTheme();

  useEffect(() => {
    if (checkActivityId) {
      dispatch(maintenanceManagementActions.getActivityRequest(checkActivityId));
    }

    return () => {
      dispatch(maintenanceManagementActions.resetActivityState());
    };
  }, [checkActivityId]);

  useEffect(() => {
    if (checkActivityId && !isEmpty(activityData)) {
      setChecklistName(activityData.checklist_name);
      setTags(
        activityData.checklist_tags.map((tag: { key: string; value: string }, idx: number) => ({ ...tag, id: idx + 1 }))
      );
      setActivitiesList(
        activityData.activities.map((activity: any, idx: number) => ({
          ...activity,
          id: idx + 1,
          documents: (activity.documents || []).map((document: string) => {
            return document;
          }),
        }))
      );
    }
  }, [checkActivityId, activityData]);

  useEffect(() => {
    if (addActivitySuccess || updateActivitySuccess) {
      setPopupStatus({
        open: true,
        message: addActivitySuccess
          ? `New ${saveAs.checklistName || checklistName} has been successfully created`
          : `${checklistName} has been successfully updated`,
      });
    }
  }, [addActivitySuccess, updateActivitySuccess]);

  const handleSubmit = () => {
    let ajvSchema: any = {};

    ajvSchema = {
      type: 'object',
      properties: {
        checklist_name: checklistNameSchema,
      },
      required: ['checklist_name'],
      additionalProperties: true,
    };

    if (tags.length) {
      ajvSchema.properties.checklist_tags = {
        type: 'array',
        uniqueItemProperties: ['key'],
        items: tags.map((item: any) => checklistTagsSchema),
      };
    }

    if (activitiesList.length) {
      ajvSchema.properties.activities = {
        type: 'array',
        items: activitiesList.map((item: any) => activitiesListSchema),
      };
    }

    const validate = ajv.compile(ajvSchema);

    const params = {
      checklist_name: saveAs.open ? saveAs.checklistName.trim() : checklistName.trim(),
      checklist_tags: tags.map((tag) => ({ key: tag.key.trim(), value: tag.value.trim() })),
      activities: activitiesList.map(({ id, documents, ...item }, idx) => ({
        ...item,
        documents: (documents || []).map((doc) => (typeof doc === 'string' ? doc : doc.name)),
        sequence: idx + 1,
      })),
      activity_documents: activitiesList.reduce((acc: any, activity) => {
        acc = [...acc, ...(activity.documents || []).map((doc) => doc.file)].filter((doc) => doc);
        return acc;
      }, []),
    };

    if (validate({ ...params })) {
      const formdata = new FormData();
      formdata.append('checklist_name', params.checklist_name);
      formdata.append('checklist_tags', JSON.stringify(params.checklist_tags));
      formdata.append('activities', JSON.stringify(params.activities));
      params.activity_documents.forEach((doc: any) => {
        formdata.append('activity_documents', doc);
      });
      if (isEdit && !saveAs.open) {
        formdata.append('checklist_id', checkActivityId);
        dispatch(maintenanceManagementActions.updateActivityRequest(formdata));
      } else {
        dispatch(maintenanceManagementActions.addActivityRequest(formdata));
      }
      setAjvErrors({});
      setSaveAs({ ...saveAs, open: false });
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  const onBackClick = () => {
    navigate('/maintenance-management/activity-list');
  };

  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.length && !viewFromModal) {
        const pathnamesTemp: any = pathname.split('/').filter(Boolean);
        if (pathnamesTemp.length) {
            const pathnamesData: any = pathnamesTemp
                .filter((i: any, index: number) => index != 3)
                .map((item: any, index: number) => {
                    if (index == 0) {
                        return { route: pathnamesTemp[0], pathName: 'Maintenance Management', navigate: true };
                    } else if (index == 1) {
                        return { route: pathnamesTemp[0]+'/'+pathnamesTemp[1], pathName: 'Maintenance Activity', navigate: true };
                    } else {
                        return { route: '', pathName: item, navigate: false };
                    }
                });
            dispatch(pageActions.setPagePath(pathnamesData));
            return () => {
                const data: any = [];
                dispatch(pageActions.setPagePath(data));
            };
        }
    }
}, [activityData, viewFromModal])

  return (
    <Container maxWidth={false} disableGutters sx={Styles.masterContainer}>
      <Paper
        sx={{
          ...Styles.masterPaperContainer,
          border: `1px solid  ${themeData.palette.customBorderColor.main}`,
          backgroundColor: 'transparent',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {(activityDataLoading || addActivityLoading || updateActivityLoading) && (
          <Box
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!viewFromModal && (
          <>
            <Grid
              container
              sx={{
                backgroundColor:
                  lightOrdarkColor(theme.palette.background.default) === 'dark'
                    ? lighten(theme.palette.background.default, 0.05)
                    : lighten(theme.palette.background.default, 0.9),
              }}
            >
              <Grid item xs={12} sx={{ p: '23px 32px', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  onClick={() => {
                    navigate('/maintenance-management/activity-list');
                  }}
                >
                  Cancel
                </Button>
                {readOnly ? (
                  <Button
                    variant='contained'
                    startIcon={<EditIcon />}
                    onClick={() => {
                      navigate(`/maintenance-management/activity-list/edit/${checkActivityId}`);
                    }}
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    {isEdit && (
                      <Button
                        variant='outlined'
                        startIcon={<SaveIcon />}
                        onClick={() => {
                          setSaveAs({ open: true, checklistName: '' });
                        }}
                      >
                        Save As
                      </Button>
                    )}
                    <Button variant='contained' startIcon={<SaveIcon />} onClick={handleSubmit}>
                      Save
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>
            <Divider />
          </>
        )}
        <Grid
          container
          sx={{
            p: '16px 32px',
            display: 'flex',
            alignItems: 'center',
          }}
          columnSpacing={4}
        >
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='subtitle1' sx={{ marginRight: '25px', whiteSpace: 'nowrap' }}>
              Checklist Name <RequiredFieldStar />
            </Typography>
            <TextField
              type='text'
              margin='normal'
              fullWidth
              id='checklist_name'
              name='checklist_name'
              variant='outlined'
              value={checklistName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setChecklistName(e.target.value)}
              disabled={readOnly}
              size='small'
              sx={{ margin: 0 }}
              placeholder='Enter Name'
              helperText={
                !saveAs.open && get(ajvErrors, 'instancePath', '').includes('checklist_name') && ajvErrors.message
              }
              error={!saveAs.open && get(ajvErrors, 'instancePath', '').includes('checklist_name')}
            />
          </Grid>
        </Grid>
        <Divider />
        <Grid
          container
          sx={{
            backgroundColor:
              lightOrdarkColor(theme.palette.background.default) === 'dark'
                ? lighten(theme.palette.background.default, 0.05)
                : lighten(theme.palette.background.default, 0.9),
            p: '16px 32px 16px 32px',
          }}
        >
          <Grid item xs={6}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Tags</Typography>
          </Grid>
        </Grid>
        {/* <Divider /> */}
        <Grid
          container
          sx={{
            backgroundColor:
              lightOrdarkColor(theme.palette.background.default) === 'dark'
                ? lighten(theme.palette.background.default, 0.05)
                : lighten(theme.palette.background.default, 0.9),
            p: '16px 32px',
          }}
          columnSpacing={4}
        >
          <Grid item xs={6} md={3}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>KEY</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>VALUE</Typography>
          </Grid>
        </Grid>
        <Divider />
        <TagsTable tags={tags} setTags={setTags} ajvErrors={ajvErrors} readOnly={readOnly} />
        {!readOnly && (
          <Grid
            container
            sx={{
              backgroundColor:
                lightOrdarkColor(theme.palette.background.default) === 'dark'
                  ? lighten(theme.palette.background.default, 0.05)
                  : lighten(theme.palette.background.default, 0.9),
              p: '16px 32px',
            }}
          >
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              {ajvErrors.instancePath && ajvErrors.instancePath === '/checklist_tags' && (
                <Typography
                  variant='body2'
                  color='error'
                  sx={{ fontWeight: '400', fontSize: '1rem', lineHeight: '22px' }}
                >
                  Tags Keys should be unique!
                </Typography>
              )}
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<PlusCircleIcon />}
                onClick={() => {
                  setTags((pre) => [...pre, { id: tags.length + 1, key: '', value: '' }]);
                }}
              >
                ADD NEW ITEM
              </Button>
            </Grid>
          </Grid>
        )}
        {!readOnly && (
          <Grid
            container
            sx={{
              p: '16px 32px',
              display: 'flex',
              alignItems: 'center',
              border: `1px solid  ${themeData.palette.customBorderColor.main}`,
              backgroundColor:
                lightOrdarkColor(theme.palette.background.default) === 'dark'
                  ? lighten(theme.palette.background.default, 0.05)
                  : lighten(theme.palette.background.default, 0.9),
            }}
          >
            <Grid item xs={12} md={8} lg={8} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Grid container sx={{ display: 'flex', alignItems: 'flex-end', rowGap: 2 }}>
                <Grid item xs={12} md={12} lg={12} xl={9}>
                  <Box sx={{ display: 'flex', alignItems: 'end', width: '100%', columnGap: 2 }}>
                    <FileExport
                      entityName='Maintenance Activities'
                      exportData={{
                        id: 0,
                        activity_name: '',
                        pre_requisites: '',
                        description: '',
                      }}
                      isTemplate
                      exportFileName='maintenance_activities_template'
                      style={{ width: '400px' }}
                    />
                    {/* <VrLine style={{ color: theme.palette.customBorderColor.main }} /> */}
                    <Box
                      sx={{
                        height: '38px',
                        width: '1px',
                        backgroundColor: theme.palette.customBorderColor.main,
                        mb: '2px',
                      }}
                    />
                    <Box sx={{ width: '100%' }}>
                      <Typography variant='subtitle1'>Add activities from excel file</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Button
                          sx={{
                            width: '100%',
                            display: 'flex',
                            padding: '0px 0 0 16px',
                            justifyContent: 'space-between',
                            borderColor: '#D9DFED',
                            textTransform: 'lowercase',
                            backgroundColor: palette.primary.contrastText,
                            ':hover': {
                              backgroundColor: `${palette.primary.contrastText} !important`,
                            },
                          }}
                          variant='outlined'
                          component='label'
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-start',
                              fontStyle: 'italic',
                              color: '#c8c8ca' ,
                              // color: '#7382A9B2',
                              flexDirection: 'row',
                              textTransform: 'capitalize',
                            }}
                          >
                            Select File
                          </div>
                          <ExcelImport
                            dataColumns={['activity_name', 'pre_requisites', 'description']}
                            data={[]}
                            excelSchema={{
                              id: 0,
                              activity_name: '',
                              pre_requisites: '',
                              description: '',
                            }}
                            hasAccess
                            getRowsOnly
                            onGetError={({ message }: { message: string }) => {
                              setExcelImportStatus({ open: true, message });
                            }}
                            onGetRows={(data: any) => {
                              const updatedRows = data.map((row: any) => {
                                delete row.hasError;
                                return {
                                  ...row,
                                  reference_doc: '',
                                };
                              });
                              setActivitiesList(
                                [...activitiesList, ...updatedRows].map((item, idx) => ({
                                  ...item,
                                  id: idx + 1,
                                }))
                              );
                            }}
                            style={{
                              border: `1px solid ${
                                readOnly ? theme.palette.customBorderColor.main : theme.palette.customBorderColor.main
                              }`,
                            }}
                          />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid
          container
          sx={{
            backgroundColor:
              lightOrdarkColor(theme.palette.background.default) === 'dark'
                ? lighten(theme.palette.background.default, 0.05)
                : lighten(theme.palette.background.default, 0.9),
            borderBottom: `1px solid ${themeData.palette.customBorderColor.main}`,
          }}
        >
          <Grid item xs={6} sx={{ p: '16px 32px' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Maintenance Activities</Typography>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', overflow: 'scroll hidden', flexDirection: 'column', mb: '-8px' }}>
          <Grid
            container
            sx={{
              p: '16px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor:
                lightOrdarkColor(theme.palette.background.default) === 'dark'
                  ? lighten(theme.palette.background.default, 0.05)
                  : lighten(theme.palette.background.default, 0.9),
              flexWrap: 'nowrap',
              minWidth: '1230px',
            }}
            columnSpacing={4}
          >
            {/* {!readOnly && <Grid item xs={0.5}></Grid>} */}
            {[
              {
                label: 'ACTIVITY NAME',
                xs: readOnly ? 3 : 2.5,
                minWidth: '250px',
                typoStyles: { pl: readOnly ? '16px' : 9 },
              },
              { label: 'PRE-REQUISITES', xs: readOnly ? 3 : 2, minWidth: '200px' },
              { label: 'DESCRIPTION', xs: readOnly ? 3 : 2, minWidth: '200px' },
              {
                label: readOnly ? 'REFERENCE DOCUMENT' : 'UPLOAD REFERENCE DOCUMENT',
                xs: readOnly ? 3 : 3.5,
                minWidth: '300px',
              },
            ].map(({ label, xs, minWidth, typoStyles }) => (
              <Grid key={label} item xs={xs} sx={{ display: 'flex', alignItems: 'center', minWidth: minWidth }}>
                <Typography variant='subtitle2' sx={{ textWrap: 'nowrap', ...typoStyles }}>
                  {label}
                </Typography>
              </Grid>
            ))}
            {!readOnly && (
              <Grid
                item
                xs={2}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, minWidth: '200px' }}
              >
                <Typography variant='subtitle2' sx={{ pl: 4, pr: 20 }}>
                  ACTIONS
                </Typography>
              </Grid>
            )}
          </Grid>
          <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
            {activitiesList.length ? (
              <Dnd
                items={activitiesList}
                setItems={setActivitiesList}
                onChangeValues={(e: ChangeEvent<HTMLInputElement>, activityId: string | number) => {
                  const { name, value } = e.target;
                  const updatedActivities = activitiesList.map((activity) => {
                    if (activity.id === activityId) {
                      return {
                        ...activity,
                        [name]: value,
                      };
                    }
                    return activity;
                  });

                  setActivitiesList(updatedActivities);
                }}
                readOnly={readOnly}
                ajvErrors={ajvErrors}
                onUploadReferenceFiles={(e: any, activityId: number) => {
                  let files = e.target.files;
                  if (files.length) {
                    const fileData = files[0];
                    // const extension = files[0].name.split('.').pop().toLowerCase();

                    // console.log({ activityId, fileData, extension });

                    const updatedActivities = activitiesList.map((activity) => {
                      if (activity.id === activityId) {
                        return {
                          ...activity,
                          documents: [
                            ...(activity.documents || []),
                            {
                              name: get(files, '[0].name', ''),
                              file: fileData,
                            },
                          ],
                        };
                      }
                      return activity;
                    });

                    setActivitiesList(updatedActivities);
                  }
                }}
                onDeleteReferenceFiles={(documentName: string, activityId: number) => {
                  const updatedActivities = activitiesList.map((activity) => {
                    if (activity.id === activityId) {
                      return {
                        ...activity,
                        documents: activity.documents.filter(
                          (doc) => (typeof doc === 'string' ? doc : get(doc, 'name')) !== documentName
                        ),
                      };
                    }
                    return activity;
                  });
                  setActivitiesList(updatedActivities);
                }}
                onDocumentClick={(document: string, activityId: number) => {
                  dispatch(
                    maintenanceManagementActions.downloadDocumentRequest({
                      section: 'activity',
                      document,
                      id: activityId,
                    })
                  );
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  pt: 4,
                  pb: 4,
                  borderTop: `1px solid ${themeData.palette.customBorderColor.main}`,
                }}
              >
                <Typography sx={{ textAlign: 'center', fontSize: '16px', fontWeight: 600 }}>No Activities</Typography>
              </Box>
            )}
          </Box>
        </Box>
        {!readOnly && (
          <Grid
            container
            sx={{
              backgroundColor:
                lightOrdarkColor(theme.palette.background.default) === 'dark'
                  ? lighten(theme.palette.background.default, 0.05)
                  : lighten(theme.palette.background.default, 0.9),
              p: '16px 32px',
              borderTop: `1px solid ${themeData.palette.customBorderColor.main}`,
            }}
          >
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<PlusCircleIcon />}
                onClick={() => {
                  const idx = activitiesList.length + 1;
                  setActivitiesList((pre) => [
                    ...pre,
                    {
                      id: idx,
                      activity_name: '',
                      pre_requisites: '',
                      description: '',
                      documents: [],
                    },
                  ]);
                }}
              >
                ADD NEW ITEM
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>
      <PopUp
        title='Activities Uploading Failed'
        open={excelImportStatus.open}
        message={excelImportStatus.message}
        cancel
        cancelLabel='Close'
        onCancel={() => {
          setExcelImportStatus({
            open: false,
            message: '',
          });
        }}
        onClose={() => {
          setExcelImportStatus({
            open: false,
            message: '',
          });
        }}
      />
      <PopUp
        title='Success'
        open={popupStatus.open}
        icon={<SuccessIcon style={{ color: palette.success.dark }} />}
        message={popupStatus.message}
        confirmation
        confirmLabel='OK'
        onConfirm={onBackClick}
        onClose={onBackClick}
      />
      <FormModal
        isOpen={saveAs.open}
        title='SAVE AS'
        customStyles={{ width: 600 }}
        handleClose={() => {
          setSaveAs({ open: false, checklistName: '' });
          setAjvErrors({});
        }}
        handleSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', pt: 1, pb: 2 }}>
          <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap', mr: 2 }}>
            Checklist Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            type='text'
            margin='normal'
            fullWidth
            id='checklist_name'
            name='checklist_name'
            variant='outlined'
            value={saveAs.checklistName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSaveAs((pre) => ({ ...pre, checklistName: e.target.value }))
            }
            disabled={readOnly}
            size='small'
            sx={{ margin: 0 }}
            helperText={get(ajvErrors, 'instancePath', '').includes('checklist_name') && ajvErrors.message}
            error={get(ajvErrors, 'instancePath', '').includes('checklist_name')}
          />
        </Box>
      </FormModal>
    </Container>
  );
};

const mapStateToProps = (state: any) => ({
  activityData: state.maintenanceManagement.activityData,
  activityDataLoading: state.maintenanceManagement.activityDataLoading,
  addActivityLoading: state.maintenanceManagement.addActivityLoading,
  addActivitySuccess: state.maintenanceManagement.addActivitySuccess,
  updateActivityLoading: state.maintenanceManagement.updateActivityLoading,
  updateActivitySuccess: state.maintenanceManagement.updateActivitySuccess,
});

export default connect(mapStateToProps)(AddEditActivity);
